/* Shopify Ajax cart + server-rendered Liquid. Native forms remain the no-JS fallback. */
(() => {
  let busy = false;
  let opener = null;
  let cartChanged = false;
  async function request(url, options) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try { const response = await fetch(url, { ...options, signal: controller.signal }); return { ok: response.ok, data: await response.json() }; }
    finally { clearTimeout(timer); }
  }
  const drawer = () => document.querySelector('[data-cart-drawer]');
  const status = text => { const el = drawer()?.querySelector('[data-cart-status]'); if (el) el.textContent = text; };
  function error(text) {
    const root = drawer();
    if (!root) return;
    const el = root.querySelector('[data-cart-error]');
    el.textContent = text;
    el.hidden = !text;
    root.querySelector('[data-cart-fallback]').hidden = !text;
  }
  function open(trigger) {
    const root = drawer();
    if (!root?.showModal) return false;
    if (!root.open) {
      opener = trigger || document.activeElement;
      root.showModal();
      root.querySelector('[data-cart-close]').focus();
    }
    return true;
  }
  function setBusy(value) {
    busy = value;
    const root = drawer();
    if (!root) return;
    root.setAttribute('aria-busy', String(value));
    root.querySelectorAll('[data-cart-quantity], [data-cart-quantity-input], [name="checkout"]').forEach(control => {
      if (value) { control.dataset.cartWasDisabled = String(control.disabled); control.disabled = true; }
      else if (control.hasAttribute('data-cart-was-disabled')) { control.disabled = control.dataset.cartWasDisabled === 'true'; delete control.dataset.cartWasDisabled; }
    });
  }
  function renderSections(sections) {
    const root = drawer();
    const markup = sections?.[root.dataset.sectionId];
    if (typeof markup !== 'string') throw new Error('Your bag could not be displayed. Open the full cart to check your items.');
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const fresh = doc.querySelector('[data-cart-drawer-content]');
    if (!fresh) throw new Error('Your bag could not be displayed. Open the full cart to check your items.');
    root.querySelector('[data-cart-drawer-content]').replaceWith(fresh);
    document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = fresh.dataset.count; });
    document.querySelectorAll('[data-cart-open]').forEach(el => { el.setAttribute('aria-label', `Cart, ${fresh.dataset.count} ${Number(fresh.dataset.count) === 1 ? 'item' : 'items'}`); });
    return Number(fresh.dataset.count);
  }
  async function refresh() {
    const root = drawer();
    const url = new URL(root.dataset.rootUrl, location.origin);
    url.searchParams.set('sections', root.dataset.sectionId);
    const response = await request(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Your bag could not be loaded. Please open the full cart.');
    return renderSections(response.data);
  }
  async function post(url, body) {
    let response;
    try {
      response = await request(url, { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body });
    } catch {
      throw new Error('We could not confirm the update. Check your bag before trying again.');
    }
    let result;
    try { result = response.data; } catch { throw new Error('We could not confirm the update. Check your bag before trying again.'); }
    if (!response.ok || result.status >= 400) throw new Error(typeof result.description === 'string' ? result.description : 'This item could not be updated. Check availability and try again.');
    return result;
  }
  function sectionFields(data) {
    data.set('sections', drawer().dataset.sectionId);
    data.set('sections_url', location.pathname);
    return data;
  }
  async function change(item, quantity, focusTarget) {
    if (busy || !Number.isInteger(quantity) || quantity < 0) return;
    const key = item.dataset.key;
    const focusKind = focusTarget?.hasAttribute('data-cart-quantity-input') ? 'input' : focusTarget?.classList.contains('bag-item__remove') ? 'remove' : focusTarget?.textContent.trim() === '+' ? 'plus' : 'minus';
    error(''); status('Updating your bag…'); setBusy(true);
    try {
      const data = sectionFields(new FormData());
      data.set('id', key); data.set('quantity', String(quantity));
      const result = await post(drawer().dataset.changeUrl, data);
      cartChanged = true;
      const count = result.sections?.[drawer().dataset.sectionId] ? renderSections(result.sections) : await refresh();
      status(quantity === 0 ? 'Item removed.' : 'Your bag is updated.');
      const fresh = [...drawer().querySelectorAll('[data-cart-item]')].find(el => el.dataset.key === key);
      const target = focusKind === 'input' ? fresh?.querySelector('input') : focusKind === 'remove' ? fresh?.querySelector('.bag-item__remove') : [...(fresh?.querySelectorAll('[data-cart-quantity]') || [])].find(el => el.textContent.trim() === (focusKind === 'plus' ? '+' : '−'));
      (target && !target.disabled ? target : drawer().querySelector('[data-cart-close]')).focus();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: count } }));
    } catch (err) {
      // A failed response can follow a server-side update. Never retry a mutation automatically.
      try { await refresh(); } catch { /* Keep the existing bag and full-cart fallback. */ }
      status(''); error(err.message);
      drawer().querySelector('[data-cart-close]').focus();
    } finally { setBusy(false); }
  }
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (event.defaultPrevented || !drawer()?.showModal || !form.matches('.product-form, .quick-view__form, .ibc-card__quick-add-form')) return;
    if (event.submitter?.closest('.shopify-payment-button')) return;
    event.preventDefault();
    if (busy || !form.reportValidity()) return;
    let message = form.querySelector('[data-add-error]');
    if (!message) { message = document.createElement('p'); message.className = 'cart-add-error'; message.setAttribute('data-add-error', ''); message.setAttribute('role', 'alert'); form.append(message); }
    message.textContent = '';
    const data = sectionFields(new FormData(form));
    const buttons = [...form.querySelectorAll('button, input, select, textarea'), ...document.querySelectorAll(`button[form="${CSS.escape(form.id)}"]`)];
    const states = buttons.map(button => ({button, disabled:button.disabled}));
    buttons.forEach(button => { button.disabled = true; });
    form.setAttribute('aria-busy', 'true'); setBusy(true);
    try {
      const result = await post(drawer().dataset.addUrl, data);
      cartChanged = true;
      const quickView = form.closest('[data-quick-view-dialog]');
      const trigger = quickView ? quickView.returnFocusTarget : event.submitter;
      quickView?.close();
      open(trigger); error(''); status('Added to your bag.');
      try { if (result.sections?.[drawer().dataset.sectionId]) renderSections(result.sections); else await refresh(); }
      catch (err) { error(err.message); }
    } catch (err) {
      message.textContent = err.message;
      const link = document.createElement('a'); link.href = drawer().dataset.rootUrl + 'cart'; link.textContent = ' View cart'; message.append(link);
    } finally {
      form.removeAttribute('aria-busy');
      states.forEach(({button, disabled}) => { button.disabled = disabled; });
      setBusy(false);
    }
  });
  document.addEventListener('click', async event => {
    const trigger = event.target.closest('[data-cart-open]');
    if (trigger && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0 && drawer()?.showModal) {
      event.preventDefault(); open(trigger);
      if (busy) return;
      error(''); status('Loading your bag…'); setBusy(true);
      try { await refresh(); status(''); } catch (err) { status(''); error(err.message); } finally { setBusy(false); }
    }
    if (event.target.closest('[data-cart-close]')) drawer()?.close();
    if (event.target === drawer()) {
      const rect = event.target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.target.close();
    }
    const button = event.target.closest('[data-cart-quantity]');
    if (button) await change(button.closest('[data-cart-item]'), Number(button.dataset.cartQuantity), button);
  });
  document.addEventListener('change', event => {
    const input = event.target;
    if (!input.matches('[data-cart-quantity-input]')) return;
    if (!input.reportValidity()) return;
    change(input.closest('[data-cart-item]'), Number(input.value), input);
  });
  document.addEventListener('close', event => {
    if (!event.target.matches('[data-cart-drawer]')) return;
    const target = opener?.isConnected && !opener.disabled ? opener : document.querySelector('[data-cart-open]');
    target?.focus();
    if (cartChanged && document.body.classList.contains('template-cart')) location.reload();
  }, true);
  document.addEventListener('keydown', event => {
    const root = drawer();
    if (!root?.open || event.key !== 'Tab') return;
    const controls = [...root.querySelectorAll('a[href],button:not(:disabled),input:not(:disabled),select:not(:disabled),[tabindex="0"]')].filter(el => el.tabIndex >= 0 && el.getClientRects().length);
    const first = controls[0], last = controls.at(-1);
    if (!first) return;
    if (event.shiftKey && (document.activeElement === first || !root.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || !root.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
  });
})();
